import generateProfileIcon from '@root/assets/img/regenerate.png';
import WorkloadVulnerabilities from '@root/assets/svg/Kubernetes/WorkloadVulnerabilities';
import info from '@root/assets/svg/workload/info.svg';
import question1 from '@root/assets/svg/workload/question1.png';
import Text from '@root/components/v2/ui-elements/Text';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import YesNo from '@root/components/YesNo';

import { Box } from '@mui/material';
import {
  severityDarkColors,
  severityLightColors,
  SeverityWithText,
} from '@root/components/bootstrapTable/badges/severity';
import commonTableColumns from '@root/components/table/commonTableColumns';
import EllipsesCountTooltip from '@root/components/v3/ellipsesCountTooltip';
import isArray from 'lodash/isArray';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';
import { ProfileStatusCard } from '../../compliance/sample';
import WorkloadIcon from '@root/assets/svg/Dashboard/WorkloadIcon';

export const findingsColumns = (handleOpenModal, setSelectedActionData, hideNodes = false) => {
  return [
    // The Workload cell already carries the account and cluster, so neither gets a column of its own.
    commonTableColumns.resources({
      field: 'workload_name',
      headerName: 'Workload',
      nameKey: 'workload_name',
      // Same cluster icon as the dashboard's Kubernetes Security Posture card, on a tint of its purple.
      icon: <WorkloadIcon width="20px" height="20px" />,
      iconBackground: 'rgba(147, 51, 234, 0.12)',
      clusterKey: 'cluster',
    }),

    {
      field: 'cve_id',
      headerName: 'CVE ID',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Box
            sx={{
              background: 'rgb(243, 244, 246)',
              padding: '5px',
              borderRadius: '.5rem',
            }}>
            <WorkloadVulnerabilities height="16px" width="16px" />
          </Box>
          <Tooltip title={params.row?.cve_id}>
            <Text fontWeight={500} style={{ width: '170px' }} className="SingleLine">
              {params.row?.cve_id}
            </Text>
          </Tooltip>
        </div>
      ),
    },
    {
      field: 'cve_type',
      headerName: 'CVE Type',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => (
        <Tooltip title={params.row?.cve_type}>
          <Text fontWeight={500} style={{ width: '170px' }} className="SingleLine">
            {params.row?.cve_type}
          </Text>
        </Tooltip>
      ),
    },

    {
      field: 'severity',
      headerName: 'CVSS Severity',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => {
        const severity = params.row?.severity?.toUpperCase();
        return (
          <Tooltip title={params.row.severity}>
            {severity !== 'INFORMATIONAL' && severity !== 'UNTRIAGED' ? (
              <SeverityWithText propsValue={params.row?.severity} />
            ) : severity === 'INFORMATIONAL' ? (
              <img src={info} style={{ height: '30px', width: '30px' }} />
            ) : (
              <img src={question1} style={{ height: '30px', width: '30px' }} />
            )}
          </Tooltip>
        );
      },
    },

    {
      field: 'cvss_score',
      headerName: 'CVSS Score',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => {
        const score =
          params.row?.cvss_score === 0 || !params.row?.cvss_score ? 0 : parseFloat(params.row?.cvss_score).toFixed(1);

        const colorSeverity = params.row?.severity;
        const backgroundDarkColor = severityDarkColors[colorSeverity?.toUpperCase()] || severityDarkColors.DEFAULT;
        const backgroundLightColor = severityLightColors[colorSeverity?.toUpperCase()] || severityLightColors.DEFAULT;

        const version = params.row?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.version
          ? `v${params.row.details.cvssMetricV31.cvssMetricV31[0].cvssData.version}`
          : params.row?.details?.Vector?.match(/CVSS:(\d+\.\d+)/)?.[1]
            ? `v${params.row.details.Vector.match(/CVSS:(\d+\.\d+)/)[1]}`
            : 'v3.0';

        return (
          <div style={{ display: 'flex', alignItems: 'center', borderRadius: '5px', overflow: 'hidden' }}>
            <div
              style={{
                backgroundColor: backgroundDarkColor,
                padding: '3px 7px',
                color: '#FFFFFF', // Dark gray for better readability on pastel backgrounds
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              {score}
            </div>
            <div
              style={{
                backgroundColor: backgroundLightColor,
                padding: '3px 7px',
                color: '#5D0E61', // Dark gray for better readability
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              {version}
            </div>
          </div>
        );
      },
    },

    {
      field: 'epss_score',
      headerName: 'EPSS Score',
      classes: 'align-middle',
      minWidth: 200,
      sortable: false,
      renderCell: params => (
        <Tooltip title={params.row?.epss_score ?? '0'}>
          <Text fontWeight={500} className="SingleLine">
            {/* The API sends epss_score as a string; NaN (empty / non-numeric) falls back to 0. */}
            {(parseFloat(params.row?.epss_score) || 0).toFixed(2)}
          </Text>
        </Tooltip>
      ),
    },

    {
      field: 'exploitable',
      headerName: 'Exploitable',
      classes: 'align-middle',
      minWidth: 200,
      sortable: false,
      renderCell: params => <Text font="medium">{params?.row?.exploitable ? <YesNo yes /> : <YesNo no />}</Text>,
    },
    {
      field: 'artifact_type',
      headerName: 'Artifact Type',
      classes: 'align-middle',
      minWidth: 250,
      sortable: false,
      renderCell: params =>
        isArray(params.row.artifact_type) ? (
          <EllipsesCountTooltip labels={params.row?.artifact_type} />
        ) : (
          <Tooltip title={params.row?.artifact_type}>
            <Text fontWeight={500} className="SingleLine">
              {params.row?.artifact_type ?? '-'}
            </Text>
          </Tooltip>
        ),
    },
    {
      field: 'artifact_info_command',
      headerName: 'Package Info Command',
      classes: 'align-middle',
      minWidth: 250,
      sortable: false,
      renderCell: params =>
        isArray(params.row.artifact_info_command) ? (
          <EllipsesCountTooltip labels={params.row?.artifact_info_command} />
        ) : (
          <Tooltip title={params.row?.artifact_info_command}>
            <Text fontWeight={500} className="SingleLine">
              {params.row?.artifact_info_command ?? '-'}
            </Text>
          </Tooltip>
        ),
    },

    {
      field: 'component',
      headerName: 'Component',
      classes: 'align-middle',
      minWidth: 250,
      sortable: false,
      renderCell: params => (
        <Tooltip title={params.row?.component}>
          <Text fontWeight={500} className="SingleLine">
            {params.row?.component ?? '-'}
          </Text>
        </Tooltip>
      ),
    },
    {
      field: 'discovered_at',
      headerName: 'Discovered At',
      renderCell: params => DateFormatPipe(params?.row?.discovered_at) ?? '-',
      width: 250,
    },
    {
      field: 'remediated_at',
      headerName: 'Remediated At',
      width: 250,
      renderCell: params =>
        DateFormatPipe(params.row?.remediated_at, false, false, false, false, params.row.remarks) ?? '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: params => <ProfileStatusCard label={params.row.status} />,
      width: 250,
    },
    {
      field: 'fix_version',
      headerName: 'Fix Version',
      renderCell: params => params?.row?.fix_version ?? '-',
      width: 250,
    },
    {
      field: 'image',
      headerName: 'Affected Image',
      width: 400,
      renderCell: params => (
        <Tooltip title={params.row?.image}>
          <Text fontWeight={500} className="SingleLine">
            {params.row?.image ?? '-'}
          </Text>
        </Tooltip>
      ),
    },

    !hideNodes && {
      field: 'affected_nodes',
      headerName: 'Affected Nodes',
      renderCell: params =>
        !params?.row?.affected_workloads ? '-' : <EllipsesCountTooltip labels={params?.row?.affected_nodes ?? []} />,
      width: 450,
    },

    {
      field: 'recommended_action',
      headerName: 'Recommended Actions',
      renderCell: params => <EllipsesCountTooltip labels={params?.row?.recommended_action ?? []} />,

      width: 250,
    },

    {
      field: 'action',
      headerName: 'Action',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => (
        <div
          data-testid="click-k8s-pages-findings-columns-255"
          data-clickable="true"
          onClick={e => {
            e.stopPropagation();
            setSelectedActionData(params.row);
            if (params.row.affected_workloads.length > 0) {
              handleOpenModal();
            }
          }}>
          <img src={generateProfileIcon} height="18px" width="18px" />
        </div>
      ),
    },
  ].filter(Boolean); // remove `false` if hideNodes = true
};
